/**
 * Per-frame audio state input + pure threat helpers.
 * Mapping from prototype snapshots lives in prototype main (or thin helpers later).
 */

import {
  integrityBandFromFrac,
  leanBand,
  type IntegrityBand,
  type LeanBand,
} from "./bands";

export type LayerTiltMap = {
  mind: number;
  heart: number;
  will: number;
};

export type AudioMissionStatus = "playing" | "won" | "lost";

/** Continuous-state features for engine.tick. */
export interface AudioFrameInput {
  integrity: number;
  maxIntegrity: number;
  tilts: LayerTiltMap;
  proclaimLock: number;
  proclaimLockMax: number;
  lieCount: number;
  willEnemyCount: number;
  willHeld: boolean;
  paused: boolean;
  status: AudioMissionStatus;
  /** Optional contest grind intensity 0–1 */
  contestIntensity?: number;
}

export function integrityFrac(frame: AudioFrameInput): number {
  const max = Math.max(1, frame.maxIntegrity);
  return Math.max(0, Math.min(1, frame.integrity / max));
}

export function leanBandsFromTilts(tilts: LayerTiltMap): {
  mind: LeanBand;
  heart: LeanBand;
  will: LeanBand;
} {
  return {
    mind: leanBand(tilts.mind),
    heart: leanBand(tilts.heart),
    will: leanBand(tilts.will),
  };
}

export function integrityBandOf(frame: AudioFrameInput): IntegrityBand {
  return integrityBandFromFrac(integrityFrac(frame));
}

export function isProclaiming(frame: AudioFrameInput, threshold = 0.05): boolean {
  return frame.proclaimLock > threshold;
}

export function proclaimLockRatio(frame: AudioFrameInput): number {
  if (frame.proclaimLockMax <= 0) return 0;
  return Math.max(
    0,
    Math.min(1, frame.proclaimLock / frame.proclaimLockMax),
  );
}

/**
 * Music / pressure threat score ∈ [0, 1] (design formula).
 *
 * threat =
 *   0.35 * (1 - integrityFrac) +
 *   0.25 * willLiePressure +
 *   0.20 * avgPositiveTiltNorm +
 *   0.20 * lieCountNorm
 */
export function threatScore(frame: AudioFrameInput): number {
  const frac = integrityFrac(frame);
  const willLiePressure =
    Math.min(1, frame.willEnemyCount / 3) *
    (frame.willHeld ? 0.35 : 1);

  const tilts = [frame.tilts.mind, frame.tilts.heart, frame.tilts.will];
  const avgPositiveTiltNorm =
    tilts.reduce((sum, t) => sum + Math.max(0, t) / 100, 0) / 3;

  const lieCountNorm = Math.min(1, frame.lieCount / 6);

  const threat =
    0.35 * (1 - frac) +
    0.25 * willLiePressure +
    0.2 * avgPositiveTiltNorm +
    0.2 * lieCountNorm;

  return Math.max(0, Math.min(1, threat));
}

/** Hermite smoothstep in [edge0, edge1] → 0…1. */
function smoothstep(edge0: number, edge1: number, x: number): number {
  if (edge0 === edge1) return x < edge0 ? 0 : 1;
  const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

/**
 * How far a layer has "fallen" to the lie side (0 = holding, 1 = lie-strong).
 * Matches lean bands: contested ≤8, lie &lt;40, lie-strong ≥40.
 * Rises through the lie band so music shifts before the hard CSS step.
 */
export function layerFallen01(tilt: number): number {
  // Soft start in mid-lie, full at lie-strong threshold (40)
  return smoothstep(12, 40, tilt);
}

/**
 * Environmental music stem mix from Mind · Heart · Will tilts.
 *
 * Journey (base of mountain → cliff → crater), not integrity.
 *
 * Two routes up the mountain (take the higher / more urgent):
 *   1. Front line: Mind fall → cliff, Heart fall → volcano
 *   2. Will threat: Will falling is a direct path to volcano even if Mind still holds
 *      (threat is still at the gate — do not stay in forest while Will is collapsing)
 *
 * Weights are normalized so they sum to 1 (constant bed loudness while crossfading).
 */
export function ambientAscentWeights(tilts: LayerTiltMap): {
  forest: number;
  cliff: number;
  volcano: number;
} {
  const mindFallen = layerFallen01(tilts.mind);
  const heartFallen = layerFallen01(tilts.heart);
  const willFallen = layerFallen01(tilts.will);

  // Stage along 0 → forest, 1 → cliff, 2 → volcano
  // Route A — Mind → Heart progression
  const byFront = mindFallen * (1 - heartFallen) + heartFallen * 2;
  // Route B — Will collapse is its own crisis (Mind strong + Will weak → volcano)
  const byWill = willFallen * 2;
  // Use the more urgent path
  const stage = Math.min(2, Math.max(byFront, byWill));

  // Triangular peaks at 0 / 1 / 2 with soft overlap
  const forest = Math.max(0, 1 - Math.abs(stage - 0));
  const cliff = Math.max(0, 1 - Math.abs(stage - 1));
  const volcano = Math.max(0, 1 - Math.abs(stage - 2));

  const sum = forest + cliff + volcano;
  if (sum < 1e-6) {
    return { forest: 1, cliff: 0, volcano: 0 };
  }
  return {
    forest: forest / sum,
    cliff: cliff / sum,
    volcano: volcano / sum,
  };
}

/** Will open + enemies present → bleed cue for StateSonifier. */
export function willBleedIntensity(frame: AudioFrameInput): number {
  if (frame.willHeld || frame.willEnemyCount <= 0) return 0;
  const countPart = Math.min(1, frame.willEnemyCount / 3);
  const tiltBoost = frame.tilts.will > 15 ? 1.15 : 1;
  return Math.max(0, Math.min(1, countPart * tiltBoost));
}
